'use client';

import { useMemo, useState } from 'react';

import { VerifiedText } from '~/components/typography/verified-text';
import { Sheet, SheetContent } from '~/components/ui';
import type { DirectSaleOfferResponseDto } from '~/lib/api';
import { useCollectionsControllerGetCollectionStickers } from '~/lib/api';

import { useSaleCountdown } from '../hooks/use-sale-countdown';

import { CollectionCardSheetContent } from '~/components/collection-card/collection-card-sheet-content';
import { RarityBadge } from '~/components/rarity-badge/rarity-badge';
import { DirectSalePurchaseButton } from './direct-sale-purchase-button';
import { SaleBuyButton } from './sale-buy-button';
import { SaleStatus } from './sale-status';
import { SaleStickerCarousel } from './sale-sticker-carousel';
import { SaleStock } from './sale-stock';
import { cn } from '@bazaar-marketplace/utils';

interface DirectSaleOfferCardProps {
  offer: DirectSaleOfferResponseDto;
}

export const DirectSaleOfferCard = ({ offer }: DirectSaleOfferCardProps) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { data: stickersData } = useCollectionsControllerGetCollectionStickers(offer.collectionId);

  const stickers = useMemo(
    () =>
      (stickersData?.data ?? []).map((sticker) => ({
        id: sticker.id,
        image: sticker.fileName,
      })),
    [stickersData],
  );

  const isInfiniteSupply = offer.infiniteSupply === 'generate_on_sale';
  const isSoldOut = offer.inStock <= 0 && !isInfiniteSupply;

  const { status, timeText } = useSaleCountdown({
    startTime: offer.startTime,
    endTime: offer.endTime,
    isSoldOut,
  });

  const handleBuyClick = () => {
    setIsSheetOpen(true);
  };

  const isFinalStatus = status === 'sold-out' || status === 'ended';

  function getTitleSize() {
    if (offer.collectionName.length > 26) {
      return 'text-[12px]';
    }

    if (offer.collectionName.length > 18) {
      return 'text-[16px]';
    }

    return 'text-[24px]';
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-[1fr_max-content] items-center">
          <div>
            <div className={cn('text-white font-bold leading-[1.3]', getTitleSize())}>{offer.collectionName}</div>
            <div className="text-white text-[14px] font-bold leading-[1.3]">
              <VerifiedText text={offer.brandName} />
            </div>
          </div>

          <SaleBuyButton
            starPricePerPack={offer.starPricePerPack}
            status={status}
            tonPricePerPack={offer.tonPricePerPack}
            onClick={handleBuyClick}
          />
        </div>

        <div className="relative">
          <RarityBadge
            className="absolute left-2.5 w-fit top-2.5 z-10 rounded-full py-1"
            rarity={offer.collectionRarity}
            textClassName="text-[11px] px-2.5 py-0 whitespace-nowrap"
          />
          <SaleStickerCarousel stickers={stickers} />
        </div>

        {isFinalStatus ? null : (
          <div className="flex gap-3">
            <SaleStatus status={status} timeText={timeText} />
            {isInfiniteSupply ? null : (
              <>
                <div className="border-l border-white/20 h-[18px]" />
                <SaleStock inStock={offer.inStock} status={status} totalSupply={offer.totalSupplyInSale} />
              </>
            )}
          </div>
        )}
      </div>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="rounded-t-3xl">
          <CollectionCardSheetContent
            bottomContent={isSheetOpen ? <DirectSalePurchaseButton offer={offer} status={status} /> : null}
            brandName={offer.brandName}
            collectionId={offer.collectionId}
            rarity={offer.collectionRarity}
            title={offer.collectionName}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};
